import { useApi } from '@gravitate-js/the-armory'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { getAPIURL } from '@utils/api'

export const useValuation = () => {
  const api = useApi({ baseURLOverride: getAPIURL() })
  const queryClient = useQueryClient()

  const getCostOverviewQuery = (date: string, market_id: string) =>
    useQuery({
      queryKey: ['costOverviewQuery', { date, market_id }],
      queryFn: ({ queryKey }) => setIdsOnRows(api, 'valuation/costing/overview', { date, market_id }, false),
      refetchOnWindowFocus: false,
      enabled: !!date,
    })

  const getCostDetailsByIdQuery = (date: string, ici_id: string) =>
    useQuery({
      queryKey: ['costDetailByIdQuery', { date, ici_id }],
      queryFn: ({ queryKey }) =>
        api.post(`valuation/costing/details`, undefined, { queryParams: new URLSearchParams({ date, ici_id }) }),
      enabled: !!date && !!ici_id,
    })

  const addManualLayerMutation = () =>
    useMutation({
      mutationKey: ['addManualLayerMutation'],
      mutationFn: (request) => api.post(`valuation/delivery/add_manual_layer`, request),
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ['costDetailByIdQuery'],
        })
      },
    })

  const addAdjustmentMutation = () =>
    useMutation({
      mutationKey: ['addAdjustmentMutation'],
      mutationFn: (request) => api.post(`valuation/delivery/adjust`, request),
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ['costDetailByIdQuery'],
        })
      },
    })

  const getHistorySelectorsQuery = () =>
    useQuery({
      queryKey: ['historySelectorsQuery'],
      queryFn: () => setIdsOnRows(api, `valuation/history/overview`, { days_back: 30 }, false),
      refetchOnWindowFocus: false,
    })

  const getHistoryByICIQuery = (ici_id: string) =>
    useQuery({
      queryKey: ['historyByICIQuery', ici_id],
      queryFn: ({ queryKey }) => setIdsOnRows(api, `valuation/history/by_ici`, { ici_id }, true),
      enabled: !!ici_id,
    })

  return {
    getCostOverviewQuery,
    getCostDetailsByIdQuery,
    addManualLayerMutation,
    addAdjustmentMutation,
    getHistorySelectorsQuery,
    getHistoryByICIQuery,
  }
}

const setIdsOnRows = async (api, url, searchParams, returnsRows) => {
  if (returnsRows) {
    const rows = await api.post(url, undefined, {
      queryParams: new URLSearchParams(searchParams),
    })
    return rows?.map((row) => {
      row.id = crypto.randomUUID()
      return row
    })
  } else {
    const data = await api.post(url, undefined, {
      queryParams: new URLSearchParams(searchParams),
    })
    data.rows = data?.rows?.map((row) => {
      row.id = crypto.randomUUID()
      return row
    })
    return data
  }
}
